import type { Database } from "../../__definitions__/database-definitions";

export type Assistant = Database["public"]["Tables"]["mcp_assistant_setup"]["Row"]["assistant"];

export type SetupField = { label: string; value: string } | { label: string; connectionUrl: true };

interface SetupStep {
  text: string;
  role?: "Admin" | "Setup owner";
  fields?: SetupField[];
}

interface SetupPath {
  id: string;
  label: string;
  steps: SetupStep[];
}

export interface AssistantInfo {
  id: Assistant;
  name: string;
  plan: string;
  logo: string;
  removePath: string;
  paths: SetupPath[];
}

const description = "Create, schedule and analyze employee comms";

const claudeDetails: SetupField[] = [
  { label: "Name", value: "ChangeEngine" },
  { label: "URL", connectionUrl: true },
];

const claudeSignIn: SetupField[] = [
  { label: "Authentication", value: "Sign in now" },
  { label: "OAuth client", value: "Register automatically" },
];

const chatgptDetails: SetupField[] = [
  { label: "Name", value: "ChangeEngine" },
  { label: "Description", value: description },
  { label: "Server URL", connectionUrl: true },
  { label: "Authentication", value: "OAuth" },
];

export const ASSISTANTS: AssistantInfo[] = [
  {
    id: "claude",
    name: "Claude",
    plan: "Free, Pro, Max, Team or Enterprise",
    logo: "https://images.changeengine.com/claude.webp",
    removePath:
      "Customize › Connectors (an Owner removes it in Organization settings › Connectors on Team or Enterprise)",
    paths: [
      {
        id: "pro_max",
        label: "Free, Pro or Max",
        steps: [
          { text: "In Claude, open Customize › Connectors." },
          { text: "Click + Add, then choose Add custom connector." },
          { text: "Enter these details, then click Continue.", fields: claudeDetails },
          {
            text: "Click Continue on the detected settings, make sure these sign-in settings are selected, then click Add.",
            fields: claudeSignIn,
          },
          { text: "Click Connect, sign in with your ChangeEngine account and click Allow." },
          { text: "Start a chat, open + › Connectors and switch ChangeEngine on." },
        ],
      },
      {
        id: "team_enterprise",
        label: "Team or Enterprise",
        steps: [
          {
            text: "An Owner opens Organization settings › Connectors and clicks Add.",
            role: "Admin",
          },
          { text: "Hover over Custom, then choose Web.", role: "Admin" },
          {
            text: "Enter these details, then click Continue.",
            fields: claudeDetails,
            role: "Admin",
          },
          {
            text: "Click Continue on the detected settings, make sure these sign-in settings are selected, then click Add.",
            fields: claudeSignIn,
            role: "Admin",
          },
          {
            text: "Everyone else opens Customize › Connectors, finds ChangeEngine and clicks Connect.",
          },
          {
            text: "Sign in with your ChangeEngine account, then switch it on in a chat from + › Connectors.",
          },
        ],
      },
    ],
  },
  {
    id: "chatgpt",
    name: "ChatGPT",
    plan: "Plus, Pro, Business, Enterprise or Edu",
    logo: "https://images.changeengine.com/chatgpt.webp",
    removePath:
      "Customize › Plugins › ChangeEngine (a workspace admin removes a published plugin from the workspace)",
    paths: [
      {
        id: "just_me",
        label: "Just for me",
        steps: [
          {
            text: "On a Business, Enterprise or Edu workspace, ask an admin to let you create plugins with MCPs in Workspace settings › Permissions & roles. Skip this on Plus or Pro.",
            role: "Admin",
          },
          { text: "In ChatGPT, open Customize › Plugins. On the web, go to chatgpt.com/plugins." },
          { text: "Click Add (+ on the web), then choose Add custom MCP server." },
          {
            text: "Enter these details. Leave Connection set to Server URL.",
            fields: chatgptDetails,
          },
          { text: "Tick I understand and want to continue, then click Create as a plugin." },
          {
            text: "In the Connect ChangeEngine window, click Continue to ChangeEngine and sign in with your ChangeEngine account.",
          },
          { text: "Review the tools and details ChatGPT found on the ChangeEngine server." },
          {
            text: "Back in Customize › Plugins, open Personal › Created by you and click + next to ChangeEngine to install it.",
          },
          { text: "Start a new chat, type @ and choose ChangeEngine." },
        ],
      },
      {
        id: "workspace",
        label: "For the whole workspace",
        steps: [
          {
            text: "In Workspace settings › Permissions & roles, give whoever sets this up these permissions: Create plugins with MCPs, Share plugins (to share with selected people) and Publish plugins to workspace (to list it for everyone).",
            role: "Admin",
          },
          {
            text: "Open Customize › Plugins (chatgpt.com/plugins on the web), click Add, then choose Add custom MCP server.",
            role: "Setup owner",
          },
          {
            text: "Enter these details. Leave Connection set to Server URL.",
            fields: chatgptDetails,
            role: "Setup owner",
          },
          {
            text: "Tick I understand and want to continue, then click Create as a plugin.",
            role: "Setup owner",
          },
          {
            text: "In the Connect ChangeEngine window, click Continue to ChangeEngine and sign in with your ChangeEngine account.",
            role: "Setup owner",
          },
          {
            text: "Review the tools and details ChatGPT found on the ChangeEngine server.",
            role: "Setup owner",
          },
          {
            text: "Under Personal › Created by you, open ChangeEngine and publish it to the workspace, or share it with the people who need it.",
            role: "Setup owner",
          },
          {
            text: "Everyone else finds ChangeEngine in the workspace tab of Customize › Plugins, or opens the shared plugin link, then installs it and completes any sign-in prompts.",
          },
          { text: "Start a new chat, type @ and choose ChangeEngine." },
        ],
      },
    ],
  },
  {
    id: "copilot",
    name: "Microsoft Copilot",
    plan: "Microsoft 365 Copilot with Copilot Studio",
    logo: "https://images.changeengine.com/copilot.webp",
    removePath: "the agent’s Tools in Copilot Studio",
    paths: [
      {
        id: "copilot_studio",
        label: "Copilot Studio",
        steps: [
          {
            text: "Sign in to Copilot Studio and create or open an agent, for example “Comms assistant”.",
            role: "Admin",
          },
          {
            text: "Open Tools, then click Add a tool › New tool › Model Context Protocol.",
            role: "Admin",
          },
          {
            text: "Enter these details.",
            fields: [
              { label: "Server name", value: "ChangeEngine" },
              { label: "Server description", value: description },
              { label: "Server URL", connectionUrl: true },
            ],
            role: "Admin",
          },
          {
            text: "Choose this authentication, then click Create.",
            fields: [
              { label: "Authentication", value: "OAuth 2.0" },
              { label: "Type", value: "Dynamic discovery" },
            ],
            role: "Admin",
          },
          {
            text: "Select Create a new connection, sign in with your ChangeEngine account, then click Add to agent.",
            role: "Admin",
          },
          {
            text: "Click Publish. Then open Channels › Teams and Microsoft 365 Copilot, keep Make agent available in Microsoft 365 Copilot selected and click Add channel.",
            role: "Admin",
          },
          {
            text: "To share it with everyone, open Availability options › Show to everyone in my org and submit it for admin approval.",
            role: "Admin",
          },
          {
            text: "Your team opens Microsoft 365 Copilot, types @, picks the agent and starts asking.",
          },
        ],
      },
    ],
  },
];

export const assistantInfo = (assistant: Assistant): AssistantInfo =>
  ASSISTANTS.find(({ id }) => id === assistant)!;

const DATA_SHARING_AGREEMENT = [
  "By clicking Continue, you agree that ChangeEngine will share data from your organization’s ChangeEngine account with the AI assistants your team connects, starting with {tool}. You also confirm you’re authorized to accept this on your organization’s behalf.",
  "You only agree once. It covers your whole organization and every assistant, so teammates won’t be asked again.",
  "Your organization is the data controller for anything shared with an AI assistant, and that assistant’s own terms apply to it. ChangeEngine isn’t responsible for how {tool} or any other assistant handles that data.",
];

export const dataSharingAgreement = (assistant: Assistant): string[] =>
  DATA_SHARING_AGREEMENT.map((paragraph) =>
    paragraph.replaceAll("{tool}", assistantInfo(assistant).name),
  );

export const STARTER_PROMPTS = [
  {
    id: "create_comms",
    kind: "Create comms",
    text: "Draft an email and a matching Slack post announcing our new hybrid-working policy, scheduled for 9am Tuesday.",
  },
  {
    id: "build_program",
    kind: "Build a program",
    text: "Build a new-starter onboarding program around start date, with a manager prep email and a 30-day survey.",
  },
  {
    id: "engagement",
    kind: "Ask about engagement",
    text: "What were our five best-performing comms last quarter, and what do they have in common?",
  },
  {
    id: "chain",
    kind: "Chain your tools",
    text: "Read our time-off policy in Google Drive and turn it into an explainer email for new joiners.",
  },
];
